import { useToast } from '@chakra-ui/react';
import { useCallback } from 'react';

import type { ChartCorrection } from './llm-tab-content-dispatch';

const TOAST_ID = 'ai-assistant-chart-corrections';

function describeOriginalTitle(originalTitle: string | null): string {
  if (!originalTitle || originalTitle.trim().length === 0) {
    return 'A chart';
  }
  return `“${originalTitle}”`;
}

function buildToastDescription(corrections: ChartCorrection[]): string {
  return corrections
    .map((correction) => {
      const subject = describeOriginalTitle(correction.originalTitle);
      return `${subject} requested ${correction.originalDescription}, which isn't a valid combination. We used ${correction.correctedDescription} instead and renamed it to “${correction.newTitle}”.`;
    })
    .join(' ');
}

/**
 * Returns a callback that surfaces silent chart fallbacks (applied during
 * `validateAndCorrectPlotOptions`) to the user via a Chakra toast.
 *
 * The hook is intentionally returning a stable callback so it can be wired
 * straight into the `onCorrections` option of `applyLlmTabContentToTabDispatch`.
 */
export function useChartCorrectionToast(): (
  corrections: ChartCorrection[]
) => void {
  const toast = useToast();

  return useCallback(
    (corrections: ChartCorrection[]) => {
      if (corrections.length === 0) {
        return;
      }
      // Avoid stacking duplicate toasts when multiple SSE `config` events
      // arrive in quick succession.
      if (toast.isActive(TOAST_ID)) {
        toast.close(TOAST_ID);
      }
      toast({
        id: TOAST_ID,
        title:
          corrections.length === 1
            ? 'We adjusted a chart'
            : `We adjusted ${corrections.length} charts`,
        description: buildToastDescription(corrections),
        status: 'info',
        variant: 'subtle',
        duration: 8000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [toast]
  );
}
